// Admin gate. Resolves the caller's admin org membership BEFORE any admin RPC fires.
// Defense in depth: if any admin RPC slipped its 42501 check, this guard already
// redirected non-admins to /app/employee.
//
// FORKED — does NOT import useEmployee or any src/lib/pulse/** module.
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

type AdminState =
  | { status: 'loading' }
  | { status: 'ready'; orgId: string }
  | { status: 'not_admin' }
  | { status: 'error'; message: string };

export function useAdminGuard(): AdminState {
  const { user, isLoading: authLoading } = useAuth();
  const [state, setState] = useState<AdminState>({ status: 'loading' });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setState({ status: 'loading' });
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('pulse_org_members')
        .select('org_id, role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();
      if (cancelled) return;
      if (error) { setState({ status: 'error', message: error.message }); return; }
      if (!data?.org_id) { setState({ status: 'not_admin' }); return; }
      setState({ status: 'ready', orgId: data.org_id });
    })();
    return () => { cancelled = true; };
  }, [user, authLoading]);

  return state;
}
